// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { PharmacyModule } from '../../api/client'
import { formatUsDateLocal } from '../../api/format'
import { friendlyError } from '../../api/errors'
import { usePharmacyModules, useSetPharmacyModule } from '../../hooks/queries/pharmacyFeatures'
import AppButton from '../ui/AppButton'
import { CheckboxField, RadioChoice } from '../ui/FormFields'
import LoadState from '../ui/LoadState'
import Notice from '../ui/Notice'
import Section from '../ui/Section'
import StatusChip from '../ui/StatusChip'
import { useToast } from '../ui/Toast'

/**
 * SaaS admin → pharmacy: module entitlement (ADR-019 D-9), on the shared kit + TanStack Query.
 * `family` shows one module and its sub-switches only (the CareRx tab: CareRx + Accreditation Audit, Quality,
 * Enforce non-lab screening — D-CRX-87 C8); `excludeFamilies` hides them from the general list (Profile tab).
 * Same endpoints as before (GET/PUT /pharmacies/{id}/modules) — moving a card changes no row.
 */
export default function PharmacyModulesCard({ pharmacyId, family, excludeFamilies = [], title, subtitle }: {
  pharmacyId: string
  family?: string
  excludeFamilies?: string[]
  title: string
  subtitle: string
}) {
  const modules = usePharmacyModules(pharmacyId)
  const all = modules.data?.modules
  const inFamily = (m: PharmacyModule, root: string) => m.module_key === root || m.parent_key === root
  const roots = (all ?? []).filter(m => !m.parent_key
    && (family ? m.module_key === family : !excludeFamilies.some(f => inFamily(m, f))))

  return (
    <Section title={title} subtitle={subtitle}>
      <LoadState query={{ ...modules, data: all }} what="the modules" empty="No modules.">
        {list => (
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {roots.map(m => <ModuleBlock key={m.module_key} m={m} all={list} pharmacyId={pharmacyId} />)}
          </Stack>
        )}
      </LoadState>
    </Section>
  )
}

/** D-POT-5: the two Plan of Treatment experiences are one exclusive choice, not two checkboxes. */
const POT_NATIVE = 'carerx_pot_native', POT_CLASSIC = 'carerx_pot_classic'
const POT_CHOICE_KEYS = new Set([POT_NATIVE, POT_CLASSIC])
type PotChoice = 'off' | typeof POT_NATIVE | typeof POT_CLASSIC

const STATUS: Record<PharmacyModule['status'], { label: string; tone: 'green' | 'amber' | 'red' | 'grey' }> = {
  active: { label: 'Enabled', tone: 'green' },
  trial: { label: 'Trial', tone: 'amber' },
  trial_expired: { label: 'Trial expired', tone: 'red' },
  disabled: { label: 'Disabled', tone: 'grey' },
}

function ModuleBlock({ m, all, pharmacyId }: { m: PharmacyModule; all: PharmacyModule[]; pharmacyId: string }) {
  const toast = useToast()
  const set = useSetPharmacyModule(pharmacyId)
  const busyKey = set.isPending ? set.variables?.key : undefined
  const save = (key: string, enabled: boolean, trialDays?: number) =>
    set.mutate({ key, enabled, trialDays }, { onError: e => toast.show(friendlyError(e), 'error') })

  const status = STATUS[m.status] ?? STATUS.disabled
  const daysLeft = m.trial_ends_at ? Math.max(0, Math.ceil((new Date(m.trial_ends_at).getTime() - Date.now()) / 86_400_000)) : 0
  const trialNote = m.trial_ends_at && m.status === 'trial'
    ? `Trial ends ${formatUsDateLocal(m.trial_ends_at)} · ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
    : m.trial_ends_at && m.status === 'trial_expired' ? `Trial ended ${formatUsDateLocal(m.trial_ends_at)}` : null
  // A module that leans on another says so the moment it is enabled (CareRx without Drug Reference has an
  // enrollment door that cannot offer a drug). Advisory only: it never enables anything on its own.
  const missing = m.effective_enabled
    ? (m.recommends ?? []).map(k => all.find(x => x.module_key === k)).filter((d): d is PharmacyModule => !!d && !d.effective_enabled)
    : []
  const children = all.filter(c => c.parent_key === m.module_key && !POT_CHOICE_KEYS.has(c.module_key))
  const potKeys = all.filter(c => c.parent_key === m.module_key && POT_CHOICE_KEYS.has(c.module_key))
  // Owner 2026-10-20 / 2026-10-26: Off | OwlRx | classic, exclusive, POT off until an admin picks one. The server
  // writes the sibling off whenever either key is set, so one call switches the pair (Off = native off).
  const potChoice: PotChoice = potKeys.find(k => k.effective_enabled)?.module_key as PotChoice | undefined ?? 'off'
  const choosePot = (v: PotChoice) => { if (v !== potChoice) save(v === 'off' ? POT_NATIVE : v, v !== 'off') }

  return (
    <Box sx={{ py: 1.25 }} data-module={m.module_key}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{m.label}</Box>
            <StatusChip status={m.status} label={status.label} tone={status.tone} />
          </Stack>
          {trialNote && <Box sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{trialNote}</Box>}
        </Box>
        {m.effective_enabled ? (
          <Stack direction="row" spacing={0.75}>
            {m.status === 'trial' && (
              <AppButton size="small" variant="outlined" disabled={!!busyKey} onClick={() => save(m.module_key, true)}>Convert to full</AppButton>
            )}
            <AppButton size="small" variant="outlined" color="error" disabled={!!busyKey} onClick={() => save(m.module_key, false)}>Disable</AppButton>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.75}>
            <AppButton size="small" variant="outlined" disabled={!!busyKey} onClick={() => save(m.module_key, true, 90)}>Start 90-day trial</AppButton>
            <AppButton size="small" variant="contained" disabled={!!busyKey} onClick={() => save(m.module_key, true)}>Enable</AppButton>
          </Stack>
        )}
      </Stack>
      {missing.map(d => (
        <Box key={d.module_key} sx={{ mt: 1 }}>
          <Notice severity="warning">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <span><b>{m.label}</b> needs <b>{d.label}</b> — it resolves every drug through it. Without it the enrollment door has no drugs to offer.</span>
              <AppButton size="small" variant="contained" disabled={!!busyKey} onClick={() => save(d.module_key, true)}>
                {busyKey === d.module_key ? 'Enabling…' : `Enable ${d.label}`}
              </AppButton>
            </Stack>
          </Notice>
        </Box>
      ))}
      {/* D-CRX-65: sub-switches nest under their parent; they only say whether the parent's tab/rule applies. */}
      {children.length > 0 && (
        <Stack sx={{ ml: 2.5, mt: 0.5 }}>
          {children.map(c => (
            <Box key={c.module_key} title={m.effective_enabled ? undefined : 'Enable the parent module first'} data-module={c.module_key}>
              <CheckboxField checked={c.effective_enabled} disabled={!!busyKey || !m.effective_enabled}
                label={`Include ${c.label}`} onChange={v => save(c.module_key, v)} />
            </Box>
          ))}
        </Stack>
      )}
      {potKeys.length === 2 && (
        <Box sx={{ ml: 2.5, mt: 0.5 }} data-module="pot-experience" title={m.effective_enabled ? undefined : 'Enable the parent module first'}>
          <RadioChoice<PotChoice> label="POT experience" value={potChoice} disabled={!!busyKey || !m.effective_enabled} onChange={choosePot}
            options={[{ value: 'off', label: 'Off' }, { value: POT_NATIVE, label: 'Plan of Treatment (OwlRx)' }, { value: POT_CLASSIC, label: 'POT classic' }]} />
        </Box>
      )}
    </Box>
  )
}
